import { useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { fab } from '@fortawesome/free-brands-svg-icons'
import { far } from '@fortawesome/free-regular-svg-icons'
import SearchIcon from 'atoms/icons/SearchIcon'
import { useDebounce } from 'custom-hooks/useDebounce'

type Props = {
  open: boolean
  onClose: () => void
  onConfirm: (name: string, icon: IconProp) => void
}

type IconProps = {
  id: string
  value: IconProp
  search: string
  onClick: (name: string, icon: IconProp) => void
}

const Icon = ({ id, value, search, onClick }: IconProps) => {
  const name = useMemo(
    () =>
      id
        .replace('fa', '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
        .toLowerCase(),
    [id]
  )
  if (search && !name.includes(search)) return null
  return (
    <div className='tooltip tooltip-bottom' data-tip={name}>
      <button className='btn btn-lg btn-outline size-16' onClick={() => onClick(name, value)}>
        <FontAwesomeIcon icon={value} size='xl' />
      </button>
    </div>
  )
}

function SelectIconModal({ open, onClose, onConfirm }: Props) {
  const ref = useRef<HTMLDialogElement>(null)
  const [searchInputValue, setSearchInputValue] = useState('')
  const debouncedSearchInputValue = useDebounce(searchInputValue, 500)

  useEffect(() => {
    if (!ref.current) return
    if (open) {
      ref.current.showModal()
    } else {
      ref.current.close()
    }
  }, [open])

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  const iconSections = useMemo(
    () => [
      {
        label: 'Sólido',
        icons: Object.entries(fas).map(([key, value]) => (
          <Icon key={key} id={key} value={value} search={debouncedSearchInputValue} onClick={onConfirm} />
        ))
      },
      {
        label: 'Regular',
        icons: Object.entries(far).map(([key, value]) => (
          <Icon key={key} id={key} value={value} search={debouncedSearchInputValue} onClick={onConfirm} />
        ))
      },
      {
        label: 'Marcas',
        icons: Object.entries(fab).map(([key, value]) => (
          <Icon key={key} id={key} value={value} search={debouncedSearchInputValue} onClick={onConfirm} />
        ))
      }
    ],
    [debouncedSearchInputValue]
  )

  return (
    <dialog
      ref={ref}
      id='select-icon-modal'
      className='modal'
      onCancel={e => {
        e.preventDefault()
        handleClose()
      }}>
      <div className='modal-box max-w-6xl'>
        <h3 className='font-bold text-lg'>Selecione uma forma</h3>
        <p className='py-4'>
          <label className='input input-bordered flex items-center gap-2'>
            <span className='text-gray-400'>
              <SearchIcon size={16} />
            </span>
            <input
              tabIndex={0}
              type='text'
              className='grow'
              placeholder='Pesquisar'
              value={searchInputValue}
              onChange={e => setSearchInputValue(e.target.value)}
            />
          </label>
          {open &&
            iconSections.map(icon => (
              <>
                <div className='divider'>{icon.label}</div>
                <div className='flex flex-wrap gap-2'>{icon.icons}</div>
              </>
            ))}
        </p>
        <div className='modal-action'>
          <form className='flex gap-2' method='dialog'>
            <button className='btn' onClick={handleClose}>
              Cancelar
            </button>
          </form>
        </div>
      </div>
      <label className='modal-backdrop' htmlFor='select-icon-modal' onClick={handleClose}>
        Cancelar
      </label>
    </dialog>
  )
}

export default SelectIconModal
